import React, { useState, useEffect, useRef } from 'react';
import { Form, Select, InputNumber, Input, Row, Col, Divider, message, Button, Card, Tag, Steps, Alert, Checkbox, Typography, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CalculatorOutlined, PlusOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const FORMAS_PAGAMENTO = ['À vista','Pix','Boleto','Cartão de crédito','10 dias','15 dias','30 dias','60 dias','90 dias','120 dias','30/60','30/60/90','10 dias fora mês de emissão da NF'];

function PropostaEditar() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const clienteIdFromUrl = searchParams.get('cliente');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proposta, setProposta] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [step, setStep] = useState(0);
  const [tipoDeslocamento, setTipoDeslocamento] = useState('perto');
  const [incluirART, setIncluirART] = useState(false);
  const [creaForaMG, setCreaForaMG] = useState(false);
  const [itensProposta, setItensProposta] = useState([]);
  const [calculo, setCalculo] = useState(null);
  const [enderecos, setEnderecos] = useState([]);
  const [calcRota, setCalcRota] = useState(false);
  const [rotaInfo, setRotaInfo] = useState('');

  const calcularDeslocamento = async () => {
    const sel = form.getFieldValue('endereco_id');
    const e = enderecos.find(x => (x._fiscal ? 'fiscal_' + (x.id || 'principal') : x.id) === sel);
    if (!e) { message.warning('Selecione o endereço de atendimento primeiro'); return; }
    const destino = [e.logradouro, e.numero, e.bairro, e.cidade, e.estado, e.cep].filter(Boolean).join(', ');
    setCalcRota(true);
    try {
      const r = await api.post('/propostas/calcular-deslocamento', { endereco: destino });
      const d = r.data;
      // sem pernoite a ida e volta se repete a cada dia de execucao (rotaVezes); com pernoite e uma viagem so
      const rotaVezes = form.getFieldValue('tipo_deslocamento') === 'viagem' ? 1 : Math.max(parseInt(form.getFieldValue('dias_execucao'), 10) || 1, 1);
      const r2 = (v) => Math.round((Number(v) || 0) * rotaVezes * 100) / 100;
      form.setFieldsValue({ distancia_km: r2(d.km_total), horas_deslocamento: r2(d.horas_total), pedagio_valor: r2(d.pedagio_total), km_viagem: d.km_total });
      setRotaInfo('Ida ' + d.km_ida + ' km / volta ' + d.km_volta + ' km · ' + d.horas_total + ' h · pedágio R$ ' + d.pedagio_total.toFixed(2).replace('.', ',') + ' por dia' + (rotaVezes > 1 ? ' × ' + rotaVezes + ' dias = ' + r2(d.km_total) + ' km · ' + r2(d.horas_total) + ' h · pedágio R$ ' + r2(d.pedagio_total).toFixed(2).replace('.', ',') : '') + ' · destino: ' + d.endereco_destino);
      message.success('Deslocamento calculado — confira os valores');
    } catch (err) { message.error((err.response && err.response.data && err.response.data.erro) || 'Erro ao calcular rota'); }
    finally { setCalcRota(false); }
  };
  const [contatos, setContatos] = useState([]);
  const [cnpjs, setCnpjs] = useState([]);
  const dadosRef = useRef({});
  const [form] = Form.useForm();
  const [formItens] = Form.useForm();

  useEffect(() => { carregarDados(); }, [id]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const rServicos = await api.get('/servicos');
      setServicos(rServicos.data);

      if (!id) {
        // Nova proposta — carregar dados do cliente se vier da URL
        if (clienteIdFromUrl) {
          const clienteData = await api.get(`/clientes/${clienteIdFromUrl}`);
          const cnpjsData = await api.get(`/clientes/${clienteIdFromUrl}/cnpjs`);
          setEnderecos(clienteData.data.enderecos || []);
          setCnpjs(cnpjsData.data || []);
          setContatos(clienteData.data.contatos || []);
        }
        setLoading(false);
        return;
      }

      const rProposta = await api.get(`/propostas/${id}`);
      const p = rProposta.data;
      setProposta(p);
      setTipoDeslocamento(p.tipo_deslocamento || 'perto');
      setIncluirART(p.incluir_art || false);
      setCreaForaMG(p.crea_fora_mg || false);

      const clienteData = await api.get(`/clientes/${p.cliente_id}`);
      const enderecosAdicionais = clienteData.data.enderecos || [];
      // Adicionar endereço fiscal do cliente como opção
      const cliente = clienteData.data;
      const endsFiscais = [];
      if (cliente.logradouro_fiscal) {
        endsFiscais.push({
          id: 'fiscal_principal',
          nome_local: 'Endereço Fiscal Principal',
          logradouro: cliente.logradouro_fiscal,
          numero: cliente.numero_fiscal,
          cidade: cliente.cidade_fiscal,
          estado: cliente.estado_fiscal,
          cep: cliente.cep_fiscal,
          _fiscal: true,
        });
      }
      setEnderecos([...endsFiscais, ...enderecosAdicionais]);
      setContatos(clienteData.data.contatos || []);
      const cnpjsData = await api.get(`/clientes/${p.cliente_id}/cnpjs`);
      setCnpjs(cnpjsData.data);

      form.setFieldsValue({
        titulo: p.titulo_contrato,
        cnpj_faturamento_id: p.cnpj_faturamento_id,
        contato_id: p.contato_id,
        endereco_id: p.endereco_id,
        tipo_deslocamento: p.tipo_deslocamento || 'perto',
        dias_execucao: p.dias_execucao,
        horas_deslocamento: parseFloat(p.horas_deslocamento) || 0,
        distancia_km: parseFloat(p.distancia_km) || 0,
        pedagio_valor: parseFloat(p.pedagio_valor) || 0,
        horas_estacionamento: parseFloat(p.horas_estacionamento) || 0,
        km_viagem: parseFloat(p.km_viagem) || 0,
        dias_totais_viagem: p.dias_totais_viagem || 0,
        diarias_aluguel_carro: p.diarias_aluguel_carro || 0,
        km_deslocamento_local: parseFloat(p.km_deslocamento_local) || 0,
        forma_pagamento: p.forma_pagamento,
        validade_dias: p.validade_dias || 15,
        desconto: parseFloat(p.desconto) || 0,
        desconto_observacao: p.desconto_observacao,
        observacoes: p.observacoes,
        cliente_id: p.cliente_id,
      });

      setItensProposta((p.itens || []).map(item => ({
        servico_id: item.servico_id,
        nome: item.nome,
        equipamento: item.equipamento,
        tipo: item.tipo,
        valor_base: parseFloat(item.valor_base),
        quantidade: item.quantidade,
      })));

      dadosRef.current = form.getFieldsValue(true);
    } catch { message.error('Erro ao carregar proposta'); }
    finally { setLoading(false); }
  };

  const adicionarItem = (values) => {
    const servico = servicos.find(s => s.id === values.servico_id);
    if (!servico) return;
    const jaExiste = itensProposta.find(i => i.servico_id === values.servico_id);
    if (jaExiste) {
      setItensProposta(itensProposta.map(i => i.servico_id === values.servico_id ? { ...i, quantidade: i.quantidade + (values.quantidade || 1) } : i));
    } else {
      setItensProposta([...itensProposta, { servico_id: servico.id, nome: servico.nome, equipamento: servico.equipamento, tipo: servico.tipo, valor_base: parseFloat(servico.valor_base), quantidade: values.quantidade || 1 }]);
    }
    formItens.resetFields();
  };

  const irParaServicos = () => {
    dadosRef.current = form.getFieldsValue(true);
    setStep(1);
  };

  const montarPayload = () => {
    const formValues = form.getFieldsValue(true);
    const d = { ...dadosRef.current, ...formValues };
    return {
      cliente_id: proposta?.cliente_id || (clienteIdFromUrl ? parseInt(clienteIdFromUrl) : null),
      titulo: d.titulo,
      cnpj_faturamento_id: d.cnpj_faturamento_id || null,
      contato_id: d.contato_id || null,
      endereco_id: (typeof d.endereco_id === 'string' && d.endereco_id.startsWith('fiscal_')) ? null : (d.endereco_id || null),
      tipo_deslocamento: d.tipo_deslocamento || 'perto',
      dias_execucao: d.dias_execucao || 1,
      horas_deslocamento: d.horas_deslocamento || 0,
      distancia_km: d.distancia_km || 0,
      pedagio_valor: d.pedagio_valor || 0,
      horas_estacionamento: d.horas_estacionamento || 0,
      km_viagem: d.km_viagem || 0,
      dias_totais_viagem: d.dias_totais_viagem || 0,
      diarias_aluguel_carro: d.diarias_aluguel_carro || 0,
      km_deslocamento_local: d.km_deslocamento_local || 0,
      crea_fora_mg: creaForaMG,
      incluir_art: incluirART,
      desconto: d.desconto || 0,
      desconto_observacao: d.desconto_observacao || null,
      forma_pagamento: d.forma_pagamento || null,
      validade_dias: d.validade_dias || 15,
      observacoes: d.observacoes || null,
      itens: itensProposta,
    };
  };

  const calcular = async () => {
    if (itensProposta.length === 0) { message.warning('Adicione pelo menos um serviço'); return; }
    try {
      const payload = montarPayload();
      const r = await api.post('/propostas/calcular', payload);
      setCalculo(r.data);
      setStep(2);
    } catch(e) { console.error('Erro calcular:', e.message, e.response?.data); message.error('Erro ao calcular proposta'); }
  };

  const salvarEdicao = async () => {
    try {
      const payload = montarPayload();
      await api.put(`/propostas/${id}`, payload);
      message.success('Proposta atualizada! Nova revisão gerada.');
      navigate(`/propostas/${id}`);
    } catch { message.error('Erro ao salvar proposta'); }
  };

  const fmt = (v) => `R$ ${parseFloat(v || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

  const servicosAgrupados = servicos.reduce((acc, s) => {
    const grupo = s.tipo === 'inspecao' ? '🔍 Inspeções' : s.tipo === 'calibracao' ? '⚙️ Calibrações' : '📋 Outros Serviços';
    if (!acc[grupo]) acc[grupo] = [];
    acc[grupo].push(s);
    return acc;
  }, {});

  if (loading) return <div style={{ padding: 24 }}>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(id ? `/propostas/${id}` : '/propostas')}>Voltar</Button>
        {id ? (
          <>
            <Title level={4} style={{ margin: 0 }}>Editar Proposta — {proposta?.numero}</Title>
            <Tag color="orange">Revisão {proposta?.versao} → {(proposta?.versao || 1) + 1}</Tag>
          </>
        ) : (
          <Title level={4} style={{ margin: 0 }}>Nova Proposta</Title>
        )}
      </div>

      {id && <Alert message="Ao salvar, uma nova revisão será gerada automaticamente." type="warning" showIcon style={{ marginBottom: 16 }} />}

      <Steps current={step} style={{ marginBottom: 24 }} items={[{ title: 'Deslocamento e Valores' }, { title: 'Serviços' }, { title: 'Revisão' }]} />

      <div style={{ display: step === 0 ? 'block' : 'none' }}>
        <Form form={form} layout="vertical" preserve={true}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="titulo" label="Título da Proposta" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cnpj_faturamento_id" label="CNPJ para Faturamento">
                  <Select allowClear>
                    {cnpjs.map(c => <Option key={c.id} value={c.id}>{c.cnpj} — {c.razao_social}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contato_id" label="Contato Responsável">
                  <Select allowClear>
                    {contatos.map(c => <Option key={c.id} value={c.id}>{c.nome} — {c.setor}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="endereco_id" label="Endereço de Atendimento">
                  <Select allowClear>
                    {enderecos.map(e => <Option key={e.id} value={e._fiscal ? 'fiscal_' + (e.id || 'principal') : e.id}>{e.nome_local} — {e.logradouro ? e.logradouro + ', ' + (e.numero||'') + ', ' : ''}{e.cidade}/{e.estado}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Deslocamento" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="tipo_deslocamento" label="Tipo de Deslocamento">
                  <Select onChange={(v) => setTipoDeslocamento(v)}>
                    <Option value="perto">Perto (sem pernoite)</Option>
                    <Option value="viagem">Viagem (com pernoite)</Option>
                    <Option value="celum">Entrega e retirada na Celum (sem deslocamento)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="dias_execucao" label="Dias de execução">
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="horas_deslocamento" label="Horas deslocamento (ida+volta)">
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} />
                </Form.Item>
              </Col>
            </Row>
            {tipoDeslocamento === 'celum' ? (<div style={{ background: '#eafaf1', border: '1px solid #7fd6a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>🏢 <b>Entrega e retirada na Celum</b> — sem custo de mobilização (km, horas, pedágio, alimentação). Os itens ficam só com o valor base.</div>) : tipoDeslocamento === 'perto' ? (
              <Row gutter={16}>
                <Col span={8}><Form.Item name="distancia_km" label="Distância km (ida+volta)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                <Col span={8}><Form.Item name="pedagio_valor" label="Pedágio R$ (ida+volta)"><InputNumber style={{ width: '100%' }} min={0} precision={2} /></Form.Item></Col>
                <Col span={24}><Space wrap><Button type="dashed" onClick={calcularDeslocamento} loading={calcRota}>📍 Calcular km, tempo e pedágio pelo Google Maps</Button>{rotaInfo && <span style={{ color: '#888', fontSize: 12 }}>{rotaInfo}</span>}</Space></Col>
                <Col span={8}><Form.Item name="horas_estacionamento" label="Horas estacionamento"><InputNumber style={{ width: '100%' }} min={0} step={0.5} /></Form.Item></Col>
              </Row>
            ) : (
              <>
                <Row gutter={16}>
                  <Col span={6}><Form.Item name="km_viagem" label="Km total viagem (ida+volta)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                  <Col span={6}><Form.Item name="dias_totais_viagem" label="Dias totais"><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                  <Col span={6}><Form.Item name="pedagio_valor" label="Pedágio R$ (total)"><InputNumber style={{ width: '100%' }} min={0} precision={2} /></Form.Item></Col>
                  <Col span={24}><Space wrap><Button type="dashed" onClick={calcularDeslocamento} loading={calcRota}>📍 Calcular km, tempo e pedágio pelo Google Maps</Button>{rotaInfo && <span style={{ color: '#888', fontSize: 12 }}>{rotaInfo}</span>}</Space></Col>
                  <Col span={6}><Form.Item name="diarias_aluguel_carro" label="Diárias aluguel carro"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="km_deslocamento_local" label="Km deslocamento local (ida+volta/dia)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                </Row>
              </>
            )}
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}>
                <div style={{ padding: '12px', background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                  <Checkbox checked={creaForaMG} onChange={(e) => setCreaForaMG(e.target.checked)}>
                    <Text strong>CREA fora de MG</Text>
                  </Checkbox>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ padding: '12px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                  <Checkbox checked={incluirART} onChange={(e) => setIncluirART(e.target.checked)}>
                    <Text strong>Incluir ART no cálculo?</Text>
                  </Checkbox>
                </div>
              </Col>
              <Col span={8}>
                <Form.Item name="forma_pagamento" label="Forma de Pagamento">
                  <Select>{FORMAS_PAGAMENTO.map(f => <Option key={f} value={f}>{f}</Option>)}</Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="validade_dias" label="Validade (dias)">
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="observacoes" label="Observações internas">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={irParaServicos}>Próximo: Serviços →</Button>
          </div>
        </Form>
      </div>

      {step === 1 && (
        <div>
          <Form form={formItens} layout="vertical" onFinish={adicionarItem}>
            <Row gutter={16} align="bottom">
              <Col span={14}>
                <Form.Item name="servico_id" label="Serviço" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="children" placeholder="Selecione o serviço">
                    {Object.entries(servicosAgrupados).map(([grupo, items]) => (
                      <Select.OptGroup key={grupo} label={grupo}>
                        {items.map(s => <Option key={s.id} value={s.id}>{s.nome}{s.equipamento ? ` — ${s.equipamento}` : ''} | {fmt(s.valor_base)}</Option>)}
                      </Select.OptGroup>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="quantidade" label="Qtd." initialValue={1}>
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label=" ">
                  <Button type="primary" htmlType="submit" icon={<PlusOutlined />} block>Adicionar</Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={8}>
              <Form form={form} layout="vertical">
                <Form.Item name="desconto" label="Desconto (R$)">
                  <InputNumber style={{ width: '100%' }} min={0} precision={2} prefix="R$" />
                </Form.Item>
              </Form>
            </Col>
            <Col span={16}>
              <Form form={form} layout="vertical">
                <Form.Item name="desconto_observacao" label="Motivo do desconto">
                  <Input />
                </Form.Item>
              </Form>
            </Col>
          </Row>
          <Divider>Serviços ({itensProposta.length})</Divider>
          {itensProposta.map((item, idx) => (
            <Card key={idx} size="small" style={{ marginBottom: 8 }} extra={<Button size="small" danger onClick={() => setItensProposta(itensProposta.filter((_, i) => i !== idx))}>Remover</Button>}>
              <Row>
                <Col span={12}><Text strong>{item.nome}</Text>{item.equipamento && <Text type="secondary"> — {item.equipamento}</Text>}</Col>
                <Col span={4}><Tag color={item.tipo === 'inspecao' ? 'blue' : item.tipo === 'calibracao' ? 'green' : 'default'}>{item.tipo === 'inspecao' ? 'Inspeção' : item.tipo === 'calibracao' ? 'Calibração' : 'Outros'}</Tag></Col>
                <Col span={4}><Text>Qtd: {item.quantidade}</Text></Col>
                <Col span={4} style={{ textAlign: 'right' }}><Text>{fmt(item.valor_base)} /un</Text></Col>
              </Row>
            </Card>
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <Button onClick={() => setStep(0)}>← Voltar</Button>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={calcular} disabled={itensProposta.length === 0}>Calcular →</Button>
          </div>
        </div>
      )}

      {step === 2 && calculo && (
        <Card>
          <Row>
            <Col span={12}>
              <Text>Mobilização total: <Text strong>{fmt(calculo.custo_mobilizacao)}</Text></Text>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <div><Text>Subtotal: <Text strong>{fmt(calculo.subtotal)}</Text></Text></div>
              {calculo.desconto > 0 && <div><Text style={{ color: 'red' }}>Desconto: - <Text strong>{fmt(calculo.desconto)}</Text></Text></div>}
              <div><Text>Imposto: <Text strong>{fmt(calculo.valor_imposto)}</Text></Text></div>
              <Divider style={{ margin: '8px 0' }} />
              <Title level={3} style={{ color: '#1a3c5e', margin: 0 }}>TOTAL: {fmt(calculo.valor_total)}</Title>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>← Voltar</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={salvarEdicao} size="large">Salvar Nova Revisão</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default PropostaEditar;
